import convert from "convert-units";

import { LayerFilterOption } from "fond/map/Legend/Adornments/LayerFilter";
import { ConfigurationUpsert, GroupConfig } from "fond/types";
import { FilterConfiguration, LayerStyle } from "fond/types/ProjectLayerConfig";
import { formatCompactNumber } from "fond/utils/number";

import { convertDensity, convertPathDensity } from "../helper";

import { FamilialLayerConfig, groupConfigTemplate, layerConfigTemplate, makeBuckets, OverlayIds, sublayerTemplate } from "./configuration";

export enum DensityLayerId {
  ADDRESS_DENSITY = "census-block-groups-address-density",
  ADDRESS_PATH_DENSITY = "census-block-groups-address-path-density",
  PATH_PER_PREMISE = "census-block-groups-path-per-premise",
}
export const densityLayerFilterOptions: LayerFilterOption[] = [
  { ID: DensityLayerId.ADDRESS_DENSITY, Label: "Addresses Per Area", Type: "Option" },
  { ID: DensityLayerId.ADDRESS_PATH_DENSITY, Label: "Addresses Per Linear Distance", Type: "Option" },
  { ID: DensityLayerId.PATH_PER_PREMISE, Label: "Linear Distance Per Address", Type: "Option" },
];

export const defaultDensityFilteredLayerIds = [DensityLayerId.ADDRESS_DENSITY];

const key = "census-block-groups";

/**
 * Create the density overlay configuration.
 * Uses the data fields "address_count", "area_metres", and "street_length_metres" that are baked
 * into the census block group tiles.
 */

export const densityOverlayConfiguration = (
  systemOfMeasurement: "imperial" | "metric"
): { configs: ConfigurationUpsert; groupConfig: GroupConfig } => {
  const { config: addressDensityLayerConfig, descendants: addressDensityDescendants } = createAddressDensityLayer(systemOfMeasurement);
  const { config: addressPathDensityLayerConfig, descendants: addressPathDensityDescendants } = createAddressPathDensityLayer(systemOfMeasurement);
  const { config: pathPerPremiseLayerConfig, descendants: pathPerPremiseDescendants } = createPathPerPremiseLayer(systemOfMeasurement);

  const layerConfigs = [addressDensityLayerConfig, addressPathDensityLayerConfig, pathPerPremiseLayerConfig];
  const descendants = [...addressDensityDescendants, ...addressPathDensityDescendants, ...pathPerPremiseDescendants];

  // Create the groups
  const densities = groupConfigTemplate(OverlayIds.DENSITIES, layerConfigs, "Densities", true, OverlayIds.ROOT);

  // Assemble everything together
  return { configs: [...descendants, ...layerConfigs], groupConfig: densities };
};

const createAddressDensityLayer = (systemOfMeasurement: "imperial" | "metric"): FamilialLayerConfig => {
  const addressDensityExpression: mapboxgl.Expression = ["/", ["get", "address_count"], ["get", "area_metres"]];

  // Lisa decreed the following buckets (in units of addresses per square mile):
  //   0-100, 100-500, 500-1000, 1000-2500, 2500+
  // The values below are expressed in units of addresses per square metre.
  const splits = [0, 0.00003861021, 0.00019305107, 0.00038610215, 0.00096525539];
  const labels = splits.map((value) => formatCompactNumber(Math.round(convertDensity(value, systemOfMeasurement))));

  const buckets = makeBuckets({
    layerId: DensityLayerId.ADDRESS_DENSITY,
    attribute: addressDensityExpression,
    labels,
    splits,
    colors: ["#d53e4f", "#f96a00", "#f5c802", "#c9e816", "#4dd53e"],
    includeLastBucket: true,
    includeNullBucket: true,
  });

  const descendants = buckets.flatMap((bucket) => {
    const styles = createDensityStyles(bucket.id, bucket.color, bucket.filter, 0.3);
    const sublayerConfig = sublayerTemplate(bucket.id, key, DensityLayerId.ADDRESS_DENSITY, bucket.label, bucket.filter, styles);
    return [...styles, sublayerConfig];
  });

  const label = systemOfMeasurement === "imperial" ? "Addresses per square mile" : "Addresses per square kilometer";
  const config = layerConfigTemplate(
    DensityLayerId.ADDRESS_DENSITY,
    key,
    label,
    [],
    buckets.map(({ id }) => id),
    OverlayIds.DENSITIES
  );

  return { config, descendants };
};

const createAddressPathDensityLayer = (systemOfMeasurement: "imperial" | "metric"): FamilialLayerConfig => {
  const pathDensityExpression: mapboxgl.Expression = ["/", ["get", "address_count"], ["get", "street_intersection_length_metres"]];

  // Lisa decreed the following buckets (in units of addresses per linear mile):
  //   0-10, 10-20, 20-50, 50-100, 100+
  // The values below are expressed in units of addresses per metre.
  const splits = [0, 0.00621372736, 0.01242745473, 0.03106863683, 0.06213727366];
  const labels = splits.map((value) => formatCompactNumber(Math.round(convertPathDensity(value, systemOfMeasurement))));

  const buckets = makeBuckets({
    layerId: DensityLayerId.ADDRESS_PATH_DENSITY,
    attribute: pathDensityExpression,
    labels,
    splits,
    colors: ["#d53e4f", "#f96a00", "#f5c802", "#c9e816", "#4dd53e"],
    includeLastBucket: true,
    includeNullBucket: true,
  });

  const descendants = buckets.flatMap((bucket) => {
    const styles = createDensityStyles(bucket.id, bucket.color, bucket.filter, 0.3);
    const sublayerConfig = sublayerTemplate(bucket.id, key, DensityLayerId.ADDRESS_PATH_DENSITY, bucket.label, bucket.filter, styles);
    return [...styles, sublayerConfig];
  });

  const label = systemOfMeasurement === "imperial" ? "Addresses per linear mile" : "Addresses per linear kilometer";

  const config = layerConfigTemplate(
    DensityLayerId.ADDRESS_PATH_DENSITY,
    key,
    label,
    [],
    buckets.map(({ id }) => id),
    OverlayIds.DENSITIES
  );

  return { config, descendants };
};

const createPathPerPremiseLayer = (systemOfMeasurement: "imperial" | "metric"): FamilialLayerConfig => {
  const pathPerPremiseExpression: mapboxgl.Expression = ["/", ["get", "street_intersection_length_metres"], ["get", "address_count"]];

  // Lisa decreed the following buckets (in units of feet per address):
  //   0-50, 50-100, 100-250, 250-500, 500+
  // The values below are expressed in units of metres per address.
  const splits = [0, 15.24, 30.48, 76.2, 152.4];
  const labels = splits.map((value) =>
    formatCompactNumber(Math.round(systemOfMeasurement === "imperial" ? convert(value).from("m").to("ft") : value))
  );

  const buckets = makeBuckets({
    layerId: DensityLayerId.PATH_PER_PREMISE,
    attribute: pathPerPremiseExpression,
    labels,
    splits,
    colors: ["#4dd53e", "#c9e816", "#f5c802", "#f96a00", "#d53e4f"],
    includeLastBucket: true,
    includeNullBucket: true,
  });

  const descendants = buckets.flatMap((bucket) => {
    const styles = createDensityStyles(bucket.id, bucket.color, bucket.filter, 0.3);
    const sublayerConfig = sublayerTemplate(bucket.id, key, DensityLayerId.PATH_PER_PREMISE, bucket.label, bucket.filter, styles);
    return [...styles, sublayerConfig];
  });

  const label = systemOfMeasurement === "imperial" ? "Linear feet per address" : "Linear meters per address";

  const config = layerConfigTemplate(
    DensityLayerId.PATH_PER_PREMISE,
    key,
    label,
    [],
    buckets.map(({ id }) => id),
    OverlayIds.DENSITIES
  );

  return { config, descendants };
};

const createDensityStyles = (layerId: string, color: string, filter?: FilterConfiguration | null, opacity?: number): LayerStyle[] => [
  {
    ID: `${layerId} - polygon - fill`,
    Name: `${layerId} - polygon - fill`,
    GlobalPosition: 1,
    ConfigurationID: layerId,
    ConfigurationType: "LAYER",
    Position: 0,
    MapboxStyle: {
      type: "fill",
      ...(filter?.Mapbox ? { filter: filter.Mapbox } : {}),
      paint: {
        "fill-opacity": ["case", ["boolean", ["feature-state", "isEditing"], false], 0, opacity ?? 0.5],
        "fill-color": ["case", ["boolean", ["feature-state", "isSelected"], false], "#FFFF00", color],
      },
    },
    RawStyles: {
      Type: "fill",
      FillOpacity: opacity ?? 0.5,
      FillColor: color,
    },
    Type: "STYLE",
  },
];
